/**
 * 모듈 목 데이터.
 * 출처: 와이어프레임 `MODS`(972행) · `MODREG`(2580행) · `CAL`(2595행) · `DIAG_ITEMS`(2559행) ·
 *       `modStale()`(2557행) · `MOD_IP`(1707행).
 *
 * ⚠ **`module_id` 로 앱 키(`cent`·`asm`·`stain`·`robot`)를 그대로 썼다 — 실물과 다르다.**
 *   🔴 **「실물 서버가 무엇을 주는지 모른다(라우트 0건)」는 옛말이다. 되살리지 마라** —
 *   `GET /modules` 가 2026-09-20 실측으로 8건을 주고, ID 는 `centrifuge-1`·`assembler-1`·
 *   `staining-1`·`ceiling-1` 꼴이다(종류마다 2대 = 살아 있는 4 + 폐기 4).
 *   ⇒ 이 파일의 짧은 키는 **목 전용**이고, 실물 경로는 서버 ID 를 그대로 쓴다.
 *   **짧은 키를 서버 ID 인 양 대응표에 넣지 마라** — 2026-09-21 에 그 캐스트를 걷어냈다.
 *   i18n 의 `module.*` 키와 맞는 쪽으로 통일했다. → docs/fixtures.md
 *
 * 🔴 `allowed_commands` 와 `stale` 은 **서버가 준 값처럼 데이터로** 담는다. 계산 함수를 두지 않는다.
 */
import type {
  CalibrationRecord,
  DiagnosticRun,
  ModuleRegistryRow,
  ModuleSnapshot,
  ModuleTimelineEntry,
} from '@ctcells/contract'

import { demoTime } from './clock.ts'

const idle = (
  module_id: string,
  module_type: ModuleSnapshot['module_type'],
  extra: Partial<ModuleSnapshot> = {},
): ModuleSnapshot => ({
  module_id,
  module_type,
  state: 'IDLE',
  control_owner: 'orchestrator',
  allowed_commands: [],
  stale: false,
  last_seen: demoTime(-1),
  active_alarm_count: 0,
  e_stop: false,
  /*
   * 🔴 **실물 모양 그대로**(2026-09-29 로컬 `GET /modules` · core v0.9.1). 서 있지 않으면 `source` 도 `null` 이다 —
   *    `'sw'` 를 채우면 목이 「소프트 정지 출처」를 말하는 꼴이 된다.
   */
  hold: { kind: 'none', source: null, since: null },
  /*
   * `interlock.doors`(정본 v0.9.1 신설 · 면마다 문) — 문을 가진 세 모듈은 앞문(센서만 · `drive: null`) ·
   * 뒷문(센서 + 모터)을 싣고, 6축은 `doors: null` 이다(실측 2026-09-29 · 원문 그대로).
   */
  interlock:
    module_type === 'collaborative_6axis'
      ? { state: 'closed', type: null, location: null, doors: null }
      : {
          state: 'closed',
          type: null,
          location: null,
          doors: [
            { face: 'front', state: 'closed', sensor: 'closed', drive: null },
            { face: 'rear', state: 'closed', sensor: 'closed', drive: 'closed' },
          ],
        },
  recovery: { attempts: 0, limit: 3, locked: false },
  current_execution: null,
  version: 3,
  /*
   * 🔴 **살아 있는 모듈은 «넷 다» 이 칸을 준다** — 실물 `{"reagents": null}` 이 바닥이고,
   *    그 장비가 쓰는 소모품만 칸이 «더» 붙는다(2026-09-21 실측 · module-core `status_body`).
   *    🔴 **`{}` 로 두면 fixture 가 실물보다 순해진다** — 「칸 없음(미탑재)」과 「칸 있고 null
   *    (모름)」을 가르는 화면 동작이 목에서 아예 안 밟힌다.
   * ⚠ `reagents` 는 **언제나 `null`** 이다(시약 lot 은 별 원장 소관). 숫자를 넣지 마라.
   */
  consumables: { reagents: null },
  /*
   * 🔴 **서버가 이 값을 «준다»**(2026-09-20 실측 — `GET /modules` 8건이 전부 싣는다).
   *    계약(정본 yaml)에는 아직 없지만 실물이 실으므로 fixture 도 **서버 모양**을 따른다
   *    (`contract/src/module.ts` 의 받는 자리 주석 · `CLAUDE.md` fixture 규칙).
   *    목이 이 칸을 빼면 「폐기 모듈을 거른다」는 화면 동작이 목에서만 통과한다.
   */
  service_state: 'active',
  /*
   * 🔴 **목록 경로는 이 키를 «늘» 싣는다**(v0.9.4 · dev2 2026-10-11 `GET /modules` 8건 — 위치를 낸 넷은 봉투, mock 넷은 `null`).
   *    `null` = 받은 위치 봉투 없음. 키 부재는 «이 경로가 싣지 않는다»(단건 · 구독)라 목록 목에서 빼지 않는다.
   */
  pose_telemetry: null,
  ...extra,
  // v0.9.3 정본 필수 칸(2026-10-07) — `null` = 능력 캐시 없음(정본 설명 그대로 · 목은 능력을 싣지 않는다).
  // ⚠ 펼침 «뒤»에 둔다 — `extra` 가 부분형이라 앞에 두면 필수 칸이 선택으로 풀린다
  consumable_kinds: extra.consumable_kinds ?? null,
  consumables_view: extra.consumables_view ?? null,
  // 폐기통 자리 — 자리 등록부(`specs/vocab/positions.yaml`)에 `<module_type>.waste_container` 가 있는 종류만 참
  has_waste_container:
    extra.has_waste_container ?? (module_type === 'assembler' || module_type === 'staining'),
})

/** 실행 중 — 원심분리기만 돌고 나머지는 대기 */
export const MODULES_RUNNING: ModuleSnapshot[] = [
  idle('cent', 'centrifuge', {
    state: 'RUNNING',
    allowed_commands: ['pause', 'stop'],
    attribution: 'recipe',
    telemetry: { rotor_rpm: 1021, rotor_rcf: 300, temperature_celsius: 21.4 },
    current_execution: {
      execution_id: 'exe-2b7d',
      sequence: 'run_centrifugation',
      batch_id: 'B-260911-02',
      step: 2,
      total: 3,
    },
    grips: [{ robot_id: 'cent.gantry', state: 'at_target' }],
    version: 27,
  }),
  /* 🔴 TC 랙은 **어셈블러** 것이다 — 종전 목은 이 칸을 염색기에 붙여 두었다 */
  idle('asm', 'assembler', { consumables: { reagents: null, tc_remaining: 9 } }),
  /*
   * 🔴 **소모품 칸은 그 장비가 «선언한 것»만 붙는다**(2026-09-21 실물 대조).
   *    염색기는 팁을 쓰므로 `tips_remaining` 이 «붙고», TC 는 안 쓰므로 `tc_remaining` 이
   *    **칸째 없다.** 종전 목은 셋을 한 모듈에 몰아 넣어(`tip_remaining`·`tip_capacity`·
   *    `tc_remaining`) **셋 다 틀렸다** — 이름이 단수였고(실물은 `tips_remaining`),
   *    총량 칸은 어느 응답에도 없으며, TC 는 어셈블러 것이다.
   */
  idle('stain', 'staining', {
    reagent_storage: {
      setpoint_celsius: 4,
      actual_celsius: 4.1,
      at_target: true,
      state: 'holding',
    },
    consumables: { reagents: null, tips_remaining: 288 },
  }),
  // 🔴 6축 로봇이 응답이 늦다 — `stale` 은 서버 판정값이다. 이 모듈의 버튼은 전부 비활성이어야 한다
  idle('robot', 'collaborative_6axis', {
    stale: true,
    last_seen: demoTime(-4),
    active_alarm_count: 1,
    grips: [],
    version: 11,
  }),
  /*
   * 🔴 **폐기된 모듈 한 대를 섞어 둔다.** 실물이 그렇다 — `GET /modules` 8건 중 넷이
   *    `service_state: 'terminated'` 이고 **살아 있는 것과 같은 종류**다(원심 2줄).
   *    목에 없으면 「폐기를 거른다」가 목에서만 통과하고 실물에서 두 줄이 뜬다.
   * ⚠ **고장(`stale`)이 아니라 폐기다.** 둘을 같은 것으로 보면 응답이 늦은 멀쩡한 장비까지
   *   화면에서 사라진다 — 거르는 축은 `service_state` 하나다.
   */
  idle('mock-cent', 'centrifuge', {
    stale: true,
    last_seen: demoTime(-72),
    service_state: 'terminated',
    /* ⚠ 폐기된 모듈은 실물이 `consumables: null` 을 준다 — 「주머니째 없다」다 */
    consumables: null,
    version: 1,
  }),
]

/** 대기 — 아무것도 안 돈다 */
export const MODULES_IDLE: ModuleSnapshot[] = [
  idle('cent', 'centrifuge'),
  idle('asm', 'assembler', { consumables: { reagents: null, tc_remaining: 9 } }),
  idle('stain', 'staining', {
    reagent_storage: {
      setpoint_celsius: 4,
      actual_celsius: 4.0,
      at_target: true,
      state: 'holding',
    },
    consumables: { reagents: null, tips_remaining: 288 },
  }),
  /*
   * 6축 위치 봉투 — 실물 꼴(dev2 2026-10-11 `robot6-1` · 이름 `<robot_id>.<이름>` · 모의 값 `pose_simulated: 1`).
   * ⚠ 값은 목 전용이다(실물 sim 은 전부 0). 관절각이 아니라 TCP 좌표 · 자세 · 부가축(상하축 · 좌우축)이다
   */
  idle('robot', 'collaborative_6axis', {
    pose_telemetry: {
      ts: demoTime(-1),
      metrics: {
        'rob6.1.tcp_x_mm': 412.5,
        'rob6.1.tcp_y_mm': -88,
        'rob6.1.tcp_z_mm': 301.2,
        'rob6.1.tcp_rx_rad': 3.1416,
        'rob6.1.tcp_ry_rad': 0,
        'rob6.1.tcp_rz_rad': 1.5708,
        'rob6.1.lift_mm': 120,
        'rob6.1.traverse_mm': 860,
        pose_simulated: 1,
      },
    },
  }),
]

/**
 * 적재 대기 — 원심분리기가 사람을 기다린다.
 *
 * 🔴 **상태값은 `WAITING_FOR_MANUAL_TRAY_LOAD` 다. `READY` 로 되돌리지 마라** (2026-09-20 · 이름 갱신 2026-09-26).
 *    ⚠ 2026-09-26 전에는 `WAITING_FOR_MANUAL_LOAD` 였다 — 은퇴 이름이다(`06 §4.1`). module-core 가 이제
 *    새 이름을 보낸다(`runtime.py` · 생성 전이표 `IDLE → WAITING_FOR_MANUAL_TRAY_LOAD`). fixture 는 실물을 따른다.
 *    `READY` 는 **와이어프레임의 내부 키**이고 **서버가 내지 않는 값**이다(`ko.ts` 의
 *    `enum.moduleState` 주석이 이미 그렇게 적고 있다). fixture 가 그 값을 쓰는 동안
 *    「데모에서는 배지가 뜨는데 실물에서는 안 뜬다」가 숨었다 — fixture 가 실물보다
 *    순하면 버그가 데모를 통과한다.
 * ⚠ 이 값은 **원심 전용**이다(`06 §4.2` 모듈별 부분집합). 염색기·어셈블러에 쓰지 마라.
 */
export const MODULES_READY: ModuleSnapshot[] = [
  idle('cent', 'centrifuge', {
    state: 'WAITING_FOR_MANUAL_TRAY_LOAD',
    allowed_commands: [],
    active_alarm_count: 1,
    current_execution: {
      execution_id: 'exe-71aa',
      sequence: 'await_manual_load',
      batch_id: 'B-260911-02',
      step: 0,
      total: 3,
    },
  }),
  ...MODULES_IDLE.slice(1),
]

/**
 * 수동 조작 — 모듈이 단독 제어(`control_owner='local'`)로 넘어갔다.
 * 🔴 그러면 **통합 UI 용 상태변경 명령이 빈다.** 서버가 그렇게 준다 — 화면이 비우는 게 아니다.
 */
export const MODULES_MANUAL: ModuleSnapshot[] = MODULES_IDLE.map((m) => ({
  ...m,
  control_owner: 'local',
  allowed_commands: [],
  attribution: 'manual',
}))

/** 안전정지 — 원심분리기가 hold(safety_stop) */
export const MODULES_ERROR: ModuleSnapshot[] = [
  idle('cent', 'centrifuge', {
    state: 'ERROR',
    allowed_commands: ['recover'],
    active_alarm_count: 1,
    e_stop: true,
    hold: { kind: 'safety_stop', source: 'hw', since: demoTime(715) },
    attribution: 'inconclusive',
    recovery: { attempts: 1, limit: 3, locked: true },
    version: 31,
  }),
  ...MODULES_IDLE.slice(1),
]

/**
 * 🔴 구독 끊김 — **모든 모듈이 stale**. 화면은 마지막 값을 현재로 보이지 않고
 *    상태변경 버튼을 전부 비활성한다. 와이어프레임에는 이 경우가 없다(README §11-3 #1).
 */
export const MODULES_STALE: ModuleSnapshot[] = MODULES_RUNNING.map((m) => ({
  ...m,
  stale: true,
  allowed_commands: [],
}))

/**
 * 모듈 표 데모 행.
 *
 * 🔴 **새 응답 칸(2026-09-26)도 실측 모양 그대로다** — `display_name`·`model`·`fw_version` 은 실모듈
 *    넷이 전부 `null` 이다. 목이 실물보다 푸짐하면 목에서만 도는 화면이 생긴다.
 *    ⚠ 미러 넷(`chamber_thermal` 등)은 표 줄에 없다 — 모듈 상세의 구성 요소 카드를 걷었다(2026-10-22).
 *
 * 🔴 **`ModuleSnapshot` 응답에 있는 값만 둔다**(2026-09-20). 예전에는 펌웨어·시퀀스 수·
 *    발견 시각·가동 시간·응답 확인·최근 진단을 지어내 채웠는데, 그 값들이 **실물에는 없어서**
 *    데모만 그럴듯하고 실물은 전부 `—` 였다. 데모가 실물보다 풍부하면 화면 설계가 거짓말이 된다.
 * ⚠ 주소(`host`·`port`)는 **상세에서만** 채운다. 목록은 그 열을 그리지 않는다.
 *
 * 🔴 **`module_type` 이 «이름»을 만든다**(2026-09-21). 종전에는 이 칸이 없어서 화면이
 *    `module_id` 를 라벨 키로 쓰는 갈래를 두고 있었고, 데모의 `module_id` 가 마침 앱 키
 *    (`cent`)라 **그 갈래가 데모에서만 맞았다** — 실물 `centrifuge-1` 에서는 제목에 원값이
 *    그대로 떴다. **fixture 가 실물보다 순해서 버그가 데모를 통과한 자리다.**
 * ⚠ `module_id` 는 여기서도 앱 키로 둔다 — 실물 꼴(`centrifuge-1`)을 쓰면 `CALIBRATIONS.target` ·
 *   `MODULE_DIAGNOSTIC_HISTORY` · 모니터링 화면이 전부 같은 축을 쓰고 있어 데모 전체가
 *   갈라진다(맨 위 파일 주석 참조). **실물 ID 꼴은 시험 fixture 가 쓴다**(`moduleDetail.test.tsx`).
 */
export const MODULE_REGISTRY: ModuleRegistryRow[] = [
  {
    module_id: 'cent',
    // 🔴 2026-09-26 실측 — 실모듈 넷은 셋 다 `null`(표시 이름·모델·펌웨어). 목도 그대로 둔다
    display_name: null,
    model: null,
    fw_version: null,
    module_type: 'centrifuge',
    state: 'RUNNING',
    stale: false,
    last_seen: '2026-09-12T09:24:10+09:00',
    control_owner: 'orchestrator',
    active_alarm_count: 0,
    interlock_state: 'closed',
    e_stop: false,
    host: '10.20.0.11',
    port: 8443,
    address_source: 'mdns',
  },
  {
    module_id: 'asm',
    // 🔴 2026-09-26 실측 — 실모듈 넷은 셋 다 `null`(표시 이름·모델·펌웨어). 목도 그대로 둔다
    display_name: null,
    model: null,
    fw_version: null,
    module_type: 'assembler',
    state: 'IDLE',
    stale: false,
    last_seen: '2026-09-12T09:24:09+09:00',
    control_owner: 'orchestrator',
    active_alarm_count: 0,
    interlock_state: 'closed',
    e_stop: false,
    host: '10.20.0.12',
    port: 8443,
    address_source: 'mdns',
  },
  {
    // 🔴 수동 등록 — 자동 발견이 이 주소를 덮지 않는다
    module_id: 'stain',
    // 🔴 2026-09-26 실측 — 실모듈 넷은 셋 다 `null`(표시 이름·모델·펌웨어). 목도 그대로 둔다
    display_name: null,
    model: null,
    fw_version: null,
    module_type: 'staining',
    // ⚠ 염색기에는 적재 대기가 없다(`06 §4.2`) — 이 모듈의 「개입 대기」는 사람 수거 대기
    //   `WAITING_FOR_MANUAL_TRAY_UNLOAD` 다(생성 전이표 `COMPLETE → WAITING_FOR_MANUAL_TRAY_UNLOAD` · 2026-09-26).
    //   종전 `WAITING_FOR_UNLOAD` 는 은퇴 이름이고(`06 §4.1`), 그 전의 `READY` 는 와이어프레임 내부 키였다
    state: 'WAITING_FOR_MANUAL_TRAY_UNLOAD',
    stale: false,
    last_seen: '2026-09-12T09:24:08+09:00',
    control_owner: 'local',
    active_alarm_count: 1,
    interlock_state: 'door',
    e_stop: false,
    host: '10.20.0.13',
    port: 8443,
    address_source: 'manual',
  },
  {
    // 응답 없음 — 마지막 값을 현재로 보이지 않는다(§5-7)
    module_id: 'robot',
    // 🔴 2026-09-26 실측 — 실모듈 넷은 셋 다 `null`(표시 이름·모델·펌웨어). 목도 그대로 둔다
    display_name: null,
    model: null,
    fw_version: null,
    module_type: 'collaborative_6axis',
    state: 'IDLE',
    stale: true,
    last_seen: '2026-09-12T09:20:41+09:00',
    control_owner: 'orchestrator',
    active_alarm_count: null,
    interlock_state: null,
    e_stop: null,
    host: '10.20.0.14',
    port: 8443,
    address_source: 'mdns',
  },
]

/**
 * 보정 기록. 🔴 **본문은 실서버 응답에서 왔다** — 2026-09-20 로컬 `ctcells-api-1` 의
 * `GET /calibrations` 5건(`fe-20260920-cal-r1-*`)을 그대로 옮겼다.
 *
 * ⚠ **종전 fixture 가 세 칸에서 실물보다 「순했다」.** 셋 다 데모만 그럴듯하게 만들던 값이다.
 *
 * | 칸 | 종전(지어낸 값) | 실물 |
 * | --- | --- | --- |
 * | `type` | `'rotor 정지 위치'` · `'3축 좌표계 정렬'` 같은 **한글 산문** | `'rotor_balance'` · `'gripper_offset'` · `'pipette_volume'` · `'tip_pickup_height'` |
 * | `result` | `'통과 · ±0.3°'` 같은 **측정 요약** | **`'pass'` / `'fail'` 두 낱말뿐** (`app/services/maintenance.py` `result_of()` 가 그 둘만 낸다) |
 * | `valid_until` | 전 건에 있음 | **없는 건이 있다 — 키째 빠진다**(`model_dump(exclude_none=True)`) |
 *
 * 🔴 **`target` 만은 «데모 모듈 키»로 바꿔 실었다.** 실물은 `'centrifuge-1'`·`'assembler-1'`·
 *    `'staining-1'` 꼴(모듈 ID)인데, 데모 모듈 fixture 의 `module_id` 는 `'cent'`·`'asm'`·
 *    `'stain'` 이고 화면이 `record.target === module.module_id` 로 짝을 맞춘다
 *    (`screens/demoData.ts` `maintenanceOf`). 데모 안에서는 그 둘이 같은 축이라 **데모 키가 맞다** —
 *    실물 ID 를 넣으면 데모에서 보정 탭이 빈 채로 뜬다.
 *    ⚠ 그래도 **실물 `target` 에 접두어가 붙는다는 사실은 여기 적어 둔다.** live 경로에서는
 *      양쪽 다 서버 값이라 그대로 맞는다.
 *
 * ⚠ **6축 로봇 기록이 없다 — 뺀 것이 아니라 실물에 없다.** 종전의 `'핸드오프 지점'` 은
 *   지어낸 종류였다. 서버가 그 종류를 낸 적이 없어 **없는 채로 둔다.**
 *
 * ⚠ 마지막 원소는 **`valid_until` 키가 없다.** 실물이 그렇게 오므로 **채우지 마라**
 *   (서버가 `model_dump(exclude_none=True)` 라 빈 칸을 `null` 로 싣지 않고 키째 뺀다).
 *
 * 🔴 **단언(`as unknown as CalibrationRecord[]`)을 되살리지 마라 — 더는 필요 없다.**
 *    종전에는 계약 타입이 `Required<CalibrationRecord>` 라 **키가 빠진 이 모양을 타입으로
 *    적을 수 없었고**, 그래서 단언으로 빠져나갔다. 그 좁히기 자체가 거짓이어서
 *    2026-09-20 걷었다(`packages/contract/src/module.ts` — 사유가 거기 적혀 있다).
 *    ⇒ 이제 **실물 모양이 단언 없이 그대로 들어간다.** 단언이 다시 필요해졌다면 그건
 *    「목이 실물과 달라졌다」는 신호이지 단언을 붙일 이유가 아니다.
 */
export const CALIBRATIONS: CalibrationRecord[] = [
  {
    calibration_id: 'd9066774-a27d-422e-8619-7af4f45252ad',
    target: 'cent',
    type: 'rotor_balance',
    performed_at: '2026-09-19T05:05:00+00:00',
    performed_by: 'dev-admin',
    valid_until: '2026-10-19T05:05:00+00:00',
    result: 'pass',
  },
  {
    calibration_id: '13c4f3c0-1770-455a-871f-4e2c74b8e85e',
    target: 'asm',
    type: 'gripper_offset',
    performed_at: '2026-09-20T00:15:00+00:00',
    performed_by: 'dev-admin',
    valid_until: '2026-10-20T00:15:00+00:00',
    result: 'pass',
  },
  {
    calibration_id: '4452e0a2-6aea-4b45-98a5-7feaac70f87b',
    target: 'stain',
    type: 'pipette_volume',
    performed_at: '2026-09-18T01:20:00+00:00',
    performed_by: 'dev-admin',
    valid_until: '2026-12-18T01:20:00+00:00',
    result: 'pass',
  },
  {
    // 🔴 유효기한이 지났다 — 시작 게이트(SVC-SCH-07)의 차단 사유가 되는 자리
    calibration_id: 'f7d119be-9d7a-41a1-9247-c27a4e58bfc6',
    target: 'stain',
    type: 'tip_pickup_height',
    performed_at: '2026-08-20T02:00:00+00:00',
    performed_by: 'dev-admin',
    valid_until: '2026-09-19T02:00:00+00:00',
    result: 'pass',
  },
  /*
   * 🔴 **`valid_until` 키가 «없는» 행. 실패한 보정이라 유효기한이 서지 않는다.**
   *    이 행이 있어야 「유효기한 없음」 경로가 데모에서도 돈다 — 종전에는 전 건에 기한이 있어
   *    그 경로가 실물에서만 나타났다(`calibrationStatus` 의 `!== null` 버그가 그래서 숨었다).
   */
  {
    calibration_id: '187557bc-83df-4e84-87a9-09b52a8125b8',
    target: 'asm',
    type: 'gripper_offset',
    performed_at: '2026-09-19T23:30:00+00:00',
    performed_by: 'dev-admin',
    result: 'fail',
  },
]

/**
 * 🔴 **계약 없음.** 자가 진단 실행 경로가 orchestrator OpenAPI 에 없다(README §10-2).
 *    화면은 만들되 실행 버튼은 실물이 생길 때까지 비활성 + 사유가 맞다. → 팀장 보고 대상.
 */
export const DIAGNOSTIC_RUN: DiagnosticRun = {
  module_id: 'cent',
  started_at: demoTime(-120),
  items: [
    { key: 'comm_latency', status: 'pass', detail: '0.4 s' },
    { key: 'power_interlock', status: 'pass', detail: null },
    { key: 'door_sensor', status: 'pass', detail: null },
    { key: 'axis_homing', status: 'running', detail: null },
    { key: 'drive_torque', status: 'pending', detail: null },
    { key: 'temperature_sensor', status: 'pending', detail: null },
    { key: 'vision_camera', status: 'pending', detail: null },
    { key: 'driver_revision', status: 'pending', detail: null },
  ],
}

/**
 * 모듈 **패드**의 「최근 진단」 한 줄(`demoData.ts` 의 정비 패널).
 *
 * 🔴 **오케스트레이터 계약에는 진단 경로가 없다.** 이 값을 **콘솔 화면에 되살리지 마라** —
 *    2026-09-20 에 모듈 상세의 「최근 진단」·「연결 이력」 카드가 이 fixture 를 **컨테이너를 거치지
 *    않고 직접** 읽는 바람에 **live 모드에서도 가짜 값이 사실처럼** 떴다. 카드는 걷어냈다.
 * ⚠ 남겨 둔 이유는 **모듈 패드**다. 거기가 보는 `module_service` 에는 `/diagnostics` 가 실재한다.
 * 🔴 「연결 이력」(`MODULE_CONNECTION_HISTORY`)은 **같이 지웠다.** 쓰는 곳이 그 카드뿐이었고,
 *    남겨 두면 다음 사람이 「있는 값」으로 착각한다.
 */
export const MODULE_DIAGNOSTIC_HISTORY: Record<string, ModuleTimelineEntry[]> = {
  cent: [
    { at_text: '09-10 09:00', title: '자가 진단 전체', detail: '통과 · 11 항목' },
    { at_text: '09-01 10:20', title: 'rotor 정지 위치', detail: '통과 · ±0.3°' },
    { at_text: '08-10 09:00', title: '자가 진단 전체', detail: '통과 · 11 항목' },
  ],
  asm: [
    { at_text: '09-10 09:05', title: '자가 진단 전체', detail: '통과 · 9 항목' },
    { at_text: '08-20 14:10', title: 'UV 노출 강도', detail: '통과 · 3.2 mW/cm²' },
  ],
  stain: [
    { at_text: '09-09 09:00', title: '자가 진단 전체', detail: '통과 · 14 항목' },
    { at_text: '08-12 09:40', title: '분주 부피', detail: '통과 · ±1.2 %' },
  ],
  robot: [
    { at_text: '08-28 16:20', title: '핸드오프 지점', detail: '통과 · 3 지점' },
    { at_text: '08-10 09:10', title: '자가 진단 전체', detail: '통과 · 8 항목' },
  ],
}
